/**
 * Topografie (TOPO) Support Registry
 *
 * Support content (hints, explanations, visuals) for TOPO families.
 *
 * Module: TOPO (Topografie)
 * Families: 23
 * Coverage: 100% (migrated: 2026-03-25)
 *
 * Created: 2026-03-25
 * Status: ✅ Content migrated from familySupportRegistry.ts (Step 4)
 */

import { FamilySupportEntry } from '../familySupportRegistry';

/**
 * TOPO Support Definitions
 *
 * Each entry provides:
 * - defaultExplanation: Student-facing "Zo werkt het" text
 * - hints: Progressive hint steps (title + content)
 * - requiresHints: Whether hints are conceptually needed
 * - requiresVisual: Whether visual aid is conceptually needed
 * - defaultVisual: Optional default visual aid reference
 */
export const TOPO_SUPPORT: Record<string, FamilySupportEntry> = {
  'TOPO-NL-PROV': {
    familyId: 'TOPO-NL-PROV',
    defaultExplanation: 'Nederland heeft 12 provincies.',
    requiresHints: false,
    requiresVisual: false,
    hints: [{
      title: 'Leerstrategie',
      content: 'Begin met de provincies die je kent en werk van daaruit.\nTip: leer ze per regio (noord, oost, zuid, west).'
    }, {
      title: '12 provincies per regio',
      content: 'Noord (3): Groningen, Friesland, Drenthe\nOost (3): Overijssel, Flevoland, Gelderland\nWest (3): Utrecht, Noord-Holland, Zuid-Holland\nZuid (3): Zeeland, Noord-Brabant, Limburg\n\nEzelsbruggetje: 4 × 3 = 12'
    }, {
      title: 'Strikvragen hoofdsteden',
      content: 'Pas op! De hoofdstad is niet altijd de grootste stad:\n• Noord-Holland → Haarlem (niet Amsterdam!)\n• Zuid-Holland → Den Haag (niet Rotterdam!)\n• Overijssel → Zwolle (niet Enschede!)\n• Gelderland → Arnhem (niet Nijmegen!)'
    }]
  },
  'TOPO-NL-WATER': {
    familyId: 'TOPO-NL-WATER',
    defaultExplanation: 'Belangrijke wateren: de Rijn, Maas, Waal, IJssel, IJsselmeer, Waddenzee, Noordzee.',
    requiresHints: false,
    requiresVisual: false,
    hints: [{
      title: 'Belangrijke wateren',
      content: 'Rivieren: Rijn, Maas, Waal, IJssel\nMeren: IJsselmeer, Markermeer\nZeeën: Noordzee, Waddenzee'
    }]
  },
  'TOPO-EU-LAND': {
    familyId: 'TOPO-EU-LAND',
    defaultExplanation: 'Ken de belangrijkste Europese landen en hun hoofdsteden.',
    requiresHints: false,
    requiresVisual: false,
    hints: [{
      title: 'Leerstrategie',
      content: 'Begin met de buurlanden: België, Duitsland, Luxemburg, Frankrijk, Verenigd Koninkrijk.'
    }]
  },
  'TOPO-EU-FYSISCH': {
    familyId: 'TOPO-EU-FYSISCH',
    defaultExplanation: 'Fysische geografie van Europa: gebergten, rivieren, zeeën.',
    requiresHints: false,
    requiresVisual: false,
    hints: [{
      title: 'Overzicht',
      content: 'Gebergten: Alpen, Pyreneeën, Karpaten\nRivieren: Donau, Rijn, Seine, Wolga\nZeeën: Middellandse Zee, Zwarte Zee, Oostzee'
    }]
  },
  'TOPO-WERELD-CONT': {
    familyId: 'TOPO-WERELD-CONT',
    defaultExplanation: 'Er zijn 7 continenten en 5 oceanen.',
    requiresHints: false,
    requiresVisual: false,
    hints: [{
      title: '7 Continenten',
      content: 'Europa, Azië, Afrika, Noord-Amerika, Zuid-Amerika, Oceanië, Antarctica.\n\n5 Oceanen: Stille Oceaan, Atlantische Oceaan, Indische Oceaan, Noordelijke IJszee, Zuidelijke IJszee.'
    }]
  },
  'TOPO-KLIMAAT': {
    familyId: 'TOPO-KLIMAAT',
    defaultExplanation: 'Klimaatzones lopen in banden rond de aarde.',
    requiresHints: false,
    requiresVisual: false,
    hints: [{
      title: 'Klimaatzones',
      content: 'Tropisch: warm en vochtig (rond de evenaar)\nGematigd: wisselende seizoenen (onze zone)\nPolair: koud (bij de polen)'
    }]
  },
  'TOPO-NL-STEDEN': {
    familyId: 'TOPO-NL-STEDEN',
    defaultExplanation: 'Ken de grote steden en provinciehoofdsteden van Nederland.',
    requiresHints: false,
    requiresVisual: false,
    hints: [{
      title: 'Grote steden',
      content: 'De Randstad: Amsterdam, Rotterdam, Den Haag, Utrecht\nNoord: Groningen, Leeuwarden\nOost: Arnhem, Zwolle\nZuid: Eindhoven, Maastricht'
    }, {
      title: 'Tip',
      content: 'Leer de provinciehoofdsteden! Let op: de hoofdstad is niet altijd de grootste stad (bijv. Haarlem, niet Amsterdam).'
    }]
  },
  'TOPO-NL-STEDEN-REGIO': {
    familyId: 'TOPO-NL-STEDEN-REGIO',
    defaultExplanation: 'Ken de steden per provincie en regio van Nederland.',
    requiresHints: false,
    requiresVisual: false,
    hints: [{
      title: 'Steden per regio',
      content: 'Twente: Enschede, Hengelo, Almelo\nAchterhoek: Doetinchem, Winterswijk\nWest-Brabant: Breda, Roosendaal\nHet Gooi: Hilversum\nZaanstreek: Zaandam'
    }, {
      title: 'Tip',
      content: 'Leer de steden per provincie.\nOnthoud waarmee steden bekend zijn: Gouda=kaas, Delft=blauw aardewerk, Heerenveen=Thialf.'
    }]
  },
  'TOPO-NL-LANDSCHAP': {
    familyId: 'TOPO-NL-LANDSCHAP',
    defaultExplanation: 'Nederland heeft verschillende landschapstypen: polders, duinen, heide, rivierengebied, Wadden.',
    requiresHints: false,
    requiresVisual: false,
    hints: [{
      title: 'Landschapstypen',
      content: 'West: polders, duinen, veenweidegebied\nNoord: Waddengebied, kleigrond\nOost: zandgrond, heide, bossen\nZuid: rivierengebied, löss (Limburg)'
    }]
  },
  'TOPO-NL-BUURLANDEN': {
    familyId: 'TOPO-NL-BUURLANDEN',
    defaultExplanation: 'Nederland grenst aan België (zuid) en Duitsland (oost). De Noordzee ligt aan de westkant.',
    requiresHints: false,
    requiresVisual: false,
    hints: [{
      title: 'Buurlanden',
      content: 'Zuid: België (Vlaanderen + Wallonië)\nOost: Duitsland\nWest/Noord: Noordzee\n\nBelgië: hoofdstad Brussel\nDuitsland: hoofdstad Berlijn'
    }]
  },
  'TOPO-NL-EILANDEN': {
    familyId: 'TOPO-NL-EILANDEN',
    defaultExplanation: 'De Waddeneilanden van west naar oost: Texel, Vlieland, Terschelling, Ameland, Schiermonnikoog.',
    requiresHints: false,
    requiresVisual: false,
    hints: [{
      title: 'Ezelsbruggetje',
      content: 'Van west naar oost:\nTexel, Vlieland, Terschelling, Ameland, Schiermonnikoog\n\nOnthoud: "Twee Vrouwen Trappen Als Schildpadden"'
    }]
  },
  'TOPO-EU-HOOFDST': {
    familyId: 'TOPO-EU-HOOFDST',
    defaultExplanation: 'Ken de hoofdsteden van de belangrijkste Europese landen.',
    requiresHints: false,
    requiresVisual: false,
    hints: [{
      title: 'West-Europa',
      content: 'Frankrijk → Parijs\nDuitsland → Berlijn\nBelgië → Brussel\nVerenigd Koninkrijk → Londen\nSpanje → Madrid\nItalië → Rome'
    }, {
      title: 'Overig Europa',
      content: 'Noorwegen → Oslo\nZweden → Stockholm\nPolen → Warschau\nOostenrijk → Wenen\nZwitserland → Bern\nGriekenland → Athene'
    }]
  },
  'TOPO-EU-GEBERGTE': {
    familyId: 'TOPO-EU-GEBERGTE',
    defaultExplanation: 'Belangrijke gebergten in Europa: Alpen, Pyreneeën, Karpaten, Scandinavisch gebergte.',
    requiresHints: false,
    requiresVisual: false,
    hints: [{
      title: 'Gebergten',
      content: 'Alpen: Zwitserland, Oostenrijk, Frankrijk, Italië\nPyreneeën: grens Frankrijk-Spanje\nKarpaten: Roemenië, Slowakije\nScandinavisch gebergte: Noorwegen, Zweden'
    }]
  },
  'TOPO-EU-RIVIEREN': {
    familyId: 'TOPO-EU-RIVIEREN',
    defaultExplanation: 'Grote rivieren in Europa: Donau, Rijn, Seine, Wolga, Elbe, Theems.',
    requiresHints: false,
    requiresVisual: false,
    hints: [{
      title: 'Rivieren en landen',
      content: 'Donau: langste rivier EU (Duitsland → Zwarte Zee)\nRijn: Zwitserland → Nederland → Noordzee\nSeine: door Parijs\nWolga: langste rivier Europa (Rusland)\nTheems: door Londen'
    }]
  },
  'TOPO-EU-ZEEEN': {
    familyId: 'TOPO-EU-ZEEEN',
    defaultExplanation: 'Zeeën rond Europa: Noordzee, Middellandse Zee, Oostzee, Zwarte Zee, Atlantische Oceaan.',
    requiresHints: false,
    requiresVisual: false,
    hints: [{
      title: 'Zeeën',
      content: 'Noordzee: tussen NL, UK, Noorwegen\nMiddellandse Zee: zuid-Europa\nOostzee: Scandinavië, Baltische staten\nZwarte Zee: Turkije, Roemenië, Oekraïne'
    }]
  },
  'TOPO-EU-KLIMAAT': {
    familyId: 'TOPO-EU-KLIMAAT',
    defaultExplanation: 'Europa heeft verschillende klimaatzones: zeeklimaat (west), landklimaat (oost), mediterraan (zuid), polair (noord).',
    requiresHints: false,
    requiresVisual: false,
    hints: [{
      title: 'Klimaatzones Europa',
      content: 'Zeeklimaat: NL, UK, België (mild, veel regen)\nLandklimaat: Polen, Rusland (koude winters, warme zomers)\nMediterraan: Spanje, Italië, Griekenland (droge zomers)\nPolair: Noord-Scandinavië (koud)'
    }]
  },
  'TOPO-EU-BUURLANDEN': {
    familyId: 'TOPO-EU-BUURLANDEN',
    defaultExplanation: 'Ken welke landen aan elkaar grenzen in Europa.',
    requiresHints: false,
    requiresVisual: false,
    hints: [{
      title: 'Buurlanden herkennen',
      content: 'Frankrijk grenst aan: België, Duitsland, Zwitserland, Italië, Spanje\nDuitsland grenst aan: NL, België, Frankrijk, Zwitserland, Oostenrijk, Polen, Denemarken, Tsjechië'
    }]
  },
  'TOPO-WERELD-LANDEN': {
    familyId: 'TOPO-WERELD-LANDEN',
    defaultExplanation: 'Ken de belangrijkste landen per continent.',
    requiresHints: false,
    requiresVisual: false,
    hints: [{
      title: 'Per continent',
      content: 'Noord-Amerika: VS, Canada, Mexico\nZuid-Amerika: Brazilië, Argentinië\nAzië: China, India, Japan, Rusland\nAfrika: Egypte, Zuid-Afrika, Nigeria'
    }]
  },
  'TOPO-WERELD-HOOFDST': {
    familyId: 'TOPO-WERELD-HOOFDST',
    defaultExplanation: 'Ken de hoofdsteden van de belangrijkste landen in de wereld.',
    requiresHints: false,
    requiresVisual: false,
    hints: [{
      title: 'Wereldhoofdsteden',
      content: 'VS → Washington D.C. (niet New York!)\nChina → Peking\nJapan → Tokio\nBrazilië → Brasília (niet Rio!)\nAustralië → Canberra (niet Sydney!)\nIndia → New Delhi'
    }, {
      title: 'Strikvragen',
      content: 'De hoofdstad is niet altijd de grootste stad!\nVS: Washington D.C. (niet New York)\nAustralië: Canberra (niet Sydney)\nTurkije: Ankara (niet Istanbul)'
    }]
  },
  'TOPO-WERELD-RIVIER': {
    familyId: 'TOPO-WERELD-RIVIER',
    defaultExplanation: 'Grote rivieren in de wereld: Nijl, Amazone, Mississippi, Yangtze, Ganges.',
    requiresHints: false,
    requiresVisual: false,
    hints: [{
      title: 'Langste rivieren',
      content: 'Nijl: Afrika (langste ter wereld)\nAmazone: Zuid-Amerika (meeste water)\nMississippi: Noord-Amerika\nYangtze: China (langste van Azië)\nGanges: India'
    }]
  },
  'TOPO-WERELD-GEBERGTE': {
    familyId: 'TOPO-WERELD-GEBERGTE',
    defaultExplanation: 'Grote gebergten in de wereld: Himalaya, Andes, Rocky Mountains, Alpen.',
    requiresHints: false,
    requiresVisual: false,
    hints: [{
      title: 'Wereldgebergten',
      content: 'Himalaya: Azië (hoogste: Mount Everest)\nAndes: Zuid-Amerika (langste)\nRocky Mountains: Noord-Amerika\nAlpen: Europa\nKilimanjaro: Afrika (vrijstaande berg)'
    }]
  },
  'TOPO-WERELD-OCEANEN': {
    familyId: 'TOPO-WERELD-OCEANEN',
    defaultExplanation: 'Er zijn 5 oceanen: Stille Oceaan (grootste), Atlantische Oceaan, Indische Oceaan, Noordelijke IJszee, Zuidelijke IJszee.',
    requiresHints: false,
    requiresVisual: false,
    hints: [{
      title: '5 Oceanen',
      content: 'Stille Oceaan: grootste, tussen Azië en Amerika\nAtlantische Oceaan: tussen Europa/Afrika en Amerika\nIndische Oceaan: tussen Afrika, Azië en Australië\nNoordelijke IJszee: rond de Noordpool\nZuidelijke IJszee: rond Antarctica'
    }]
  },
  'TOPO-WERELD-KLIMAAT': {
    familyId: 'TOPO-WERELD-KLIMAAT',
    defaultExplanation: 'Klimaatzones van de wereld: tropisch (evenaar), woestijn, gematigd, polair.',
    requiresHints: false,
    requiresVisual: false,
    hints: [{
      title: 'Klimaatzones',
      content: 'Tropisch: rond de evenaar (warm, vochtig)\nWoestijn: Sahara, Arabisch (droog, heet)\nGematigd: Europa, VS (seizoenen)\nPolair: Noordpool, Zuidpool (koud)'
    }, {
      title: 'Breedteligging',
      content: 'Hoe dichter bij de evenaar, hoe warmer.\nHoe dichter bij de polen, hoe kouder.\nHoogte speelt ook mee: bergen zijn kouder.'
    }]
  }
};

/**
 * Module Statistics
 * Auto-calculated from support entries
 */
export const TOPO_SUPPORT_STATS = {
  moduleCode: 'TOPO',
  moduleName: 'Topografie',
  totalFamilies: Object.keys(TOPO_SUPPORT).length,
  withHints: Object.values(TOPO_SUPPORT).filter(e => e.hints?.length).length,
  withVisuals: Object.values(TOPO_SUPPORT).filter(e => e.defaultVisual).length,
  withExplanations: Object.values(TOPO_SUPPORT).filter(e => e.defaultExplanation).length,
  requiresHintsCount: Object.values(TOPO_SUPPORT).filter(e => e.requiresHints).length,
  requiresVisualCount: Object.values(TOPO_SUPPORT).filter(e => e.requiresVisual).length
};